import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import { config } from '../config/env';
import TimePickerStep from '../assets/components/ScheduleTour/TimePickerStep';
import ResponsiveAppBar from '../assets/components/ResponsiveAppBar';
import ContactFooter from './ContactFooter';
import Footer from './Footer';

interface TourDetails {
    schoolName: string;
    schoolKey: string;
    tourDate: string;
    tourTime: string;
    childFirstName: string;
    childLastName: string;
    parentFirstName: string;
    parentLastName: string;
    parentEmail: string;
    parentPhone: string;
    schoolAddress?: string | null;
    schoolCity?: string | null;
    schoolState?: string | null;
    schoolZipCode?: string | null;
    tourLengthMinutes: number;
}

type ViewMode = 'details' | 'reschedule';

function TourManagement() {
    const { token } = useParams<{ token: string }>();
    const [tourDetails, setTourDetails] = useState<TourDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('details');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [shouldSubmitReschedule, setShouldSubmitReschedule] = useState(false);

    const fetchTourDetails = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const url = new URL(`/v1/tour/${token}`, config.apiBaseUrl);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Referer': window.location.href,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch tour details');
            }

            const data = await response.json();
            setTourDetails(data.message);
        } catch (err) {
            setError('Tour not found or has already been cancelled.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            void fetchTourDetails();
        }
    }, [token, fetchTourDetails]);

    const handleCancelTour = async () => {
        setCancelDialogOpen(false);
        setActionLoading(true);
        setError('');

        try {
            const url = new URL(`/v1/tour/${token}/cancel`, config.apiBaseUrl);

            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Referer': window.location.href,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to cancel tour');
            }

            setSuccessMessage('Your tour has been cancelled successfully.');
            setTourDetails(null);
        } catch (err) {
            setError('Failed to cancel tour. Please try again.');
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const performReschedule = useCallback(async () => {
        if (!selectedDate || !selectedTime) {
            setError('Please select a new date and time for your tour.');
            return;
        }

        setActionLoading(true);
        setError('');

        try {
            const url = new URL(`/v1/tour/${token}/reschedule`, config.apiBaseUrl);

            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Referer': window.location.href,
                },
                body: JSON.stringify({
                    tourDate: selectedDate,
                    tourTime: selectedTime,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to reschedule tour');
            }

            setSuccessMessage('Your tour has been rescheduled successfully!');
            await fetchTourDetails();
            setViewMode('details');
            setSelectedDate('');
            setSelectedTime('');
        } catch (err) {
            const error = err as Error;
            setError(error.message || 'Failed to reschedule tour. Please try again.');
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    }, [selectedDate, selectedTime, token, fetchTourDetails]);

    // Trigger reschedule when date and time are set
    useEffect(() => {
        if (shouldSubmitReschedule && selectedDate && selectedTime) {
            setShouldSubmitReschedule(false);
            void performReschedule();
        }
    }, [shouldSubmitReschedule, selectedDate, selectedTime, performReschedule]);

    const handleRescheduleTour = () => {
        // Set flag to trigger the useEffect which will perform the actual reschedule
        setShouldSubmitReschedule(true);
    };

    const updateFormData = (data: { tourDate?: string; tourTime?: string }) => {
        if (data.tourDate) setSelectedDate(data.tourDate);
        if (data.tourTime) setSelectedTime(data.tourTime);
    };

    const handleAddToCalendar = () => {
        if (!tourDetails) return;

        // Parse the date and time
        const tourDate = new Date(tourDetails.tourDate);
        const [time, period] = tourDetails.tourTime.split(' ');
        const [hours, minutes] = time.split(':').map(Number);

        // Convert to 24-hour format
        let adjustedHours = hours;
        if (period === 'PM' && hours !== 12) adjustedHours += 12;
        if (period === 'AM' && hours === 12) adjustedHours = 0;

        tourDate.setHours(adjustedHours, minutes, 0, 0);

        // Calculate end time using the tour length from backend
        const endDate = new Date(tourDate.getTime() + tourDetails.tourLengthMinutes * 60 * 1000);

        // Format dates for iCalendar (YYYYMMDDTHHmmss)
        const formatDate = (date: Date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        // Build full address for location (without school name since it's in the title)
        const locationParts = [];
        if (tourDetails.schoolAddress) {
            locationParts.push(tourDetails.schoolAddress);
        }
        if (tourDetails.schoolCity) {
            locationParts.push(tourDetails.schoolCity);
        }
        if (tourDetails.schoolState) {
            locationParts.push(tourDetails.schoolState);
        }
        if (tourDetails.schoolZipCode) {
            locationParts.push(tourDetails.schoolZipCode);
        }
        // Join with comma and space - ICS standard format for address components
        const fullAddress = locationParts.length > 0 ? locationParts.join(' ') : tourDetails.schoolName;

        // Create iCalendar content
        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Prodigy Learn//Tour Scheduler//EN',
            'BEGIN:VEVENT',
            `DTSTART:${formatDate(tourDate)}`,
            `DTEND:${formatDate(endDate)}`,
            `SUMMARY:Tour at ${tourDetails.schoolName}`,
            `DESCRIPTION:School tour for ${tourDetails.childFirstName} ${tourDetails.childLastName}`,
            `LOCATION:${fullAddress}`,
            'STATUS:CONFIRMED',
            'END:VEVENT',
            'END:VCALENDAR',
        ].join('\r\n');

        // Create a blob and download link
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `tour-${tourDetails.schoolName.replace(/\s+/g, '-')}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    return (
        <>
            <ResponsiveAppBar />
            <Box className="tile" sx={{ mb: 20 }}>
                <br />
                <Typography
                    sx={{
                        marginTop: 'unset',
                        textAlign: 'center',
                        fontSize: { md: '4rem', xs: '2.5rem' },
                        fontWeight: '900',
                    }}
                >
                    Manage Your Tour
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mt: 5,
                        px: { xs: 2, md: 5 },
                        maxWidth: viewMode === 'reschedule' ? '900px' : '600px',
                        mx: 'auto',
                        width: '100%',
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                            <CircularProgress />
                        </Box>
                    ) : successMessage && !tourDetails ? (
                        <Box sx={{ width: '100%' }}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {successMessage}
                            </Alert>
                            <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
                                If you need to schedule a new tour, please visit our website.
                            </Typography>
                        </Box>
                    ) : error && !tourDetails ? (
                        <Alert severity="error">{error}</Alert>
                    ) : !tourDetails ? null : (
                        <Box sx={{ width: '100%' }}>
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {viewMode === 'details' ? (
                    <>
                        <Card
                            sx={{
                                width: '100%',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                boxShadow: 3,
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Tour Details
                                </Typography>

                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
                                    {/* School */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <LocationOnIcon sx={{ color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                School
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {tourDetails.schoolName}
                                            </Typography>
                                            {tourDetails.schoolAddress && (
                                                <>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {tourDetails.schoolAddress}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {[tourDetails.schoolCity, tourDetails.schoolState, tourDetails.schoolZipCode]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Date */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <CalendarTodayIcon sx={{ color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {new Date(tourDetails.tourDate).toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Time */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <AccessTimeIcon sx={{ color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Time
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {tourDetails.tourTime}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Contact */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <PersonIcon sx={{ color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Contact
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {tourDetails.parentFirstName} {tourDetails.parentLastName}
                                            </Typography>
                                            <Typography variant="body2">{tourDetails.parentEmail}</Typography>
                                            <Typography variant="body2">{tourDetails.parentPhone}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', gap: 2, mt: 3, width: '100%' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleAddToCalendar}
                                startIcon={<EventIcon />}
                                fullWidth
                                size="large"
                            >
                                Add to Calendar
                            </Button>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setCancelDialogOpen(true)}
                                disabled={actionLoading}
                                fullWidth
                                size="large"
                            >
                                Cancel Tour
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setViewMode('reschedule')}
                                disabled={actionLoading}
                                fullWidth
                                size="large"
                            >
                                Reschedule Tour
                            </Button>
                        </Box>

                        {/* Cancel Confirmation Dialog */}
                        <Dialog
                            open={cancelDialogOpen}
                            onClose={() => setCancelDialogOpen(false)}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle sx={{ fontWeight: 'bold' }}>
                                Cancel Tour?
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant="body1">
                                    Are you sure you want to cancel this tour? This action cannot be undone.
                                </Typography>
                                <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                        {tourDetails.schoolName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {new Date(tourDetails.tourDate).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })} at {tourDetails.tourTime}
                                    </Typography>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3 }}>
                                <Button
                                    onClick={() => setCancelDialogOpen(false)}
                                    variant="outlined"
                                    size="large"
                                >
                                    Keep Tour
                                </Button>
                                <Button
                                    onClick={handleCancelTour}
                                    variant="contained"
                                    color="error"
                                    size="large"
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? <CircularProgress size={24} /> : 'Cancel Tour'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                ) : (
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                            Reschedule Your Tour
                        </Typography>

                        <TimePickerStep
                            school={tourDetails.schoolKey}
                            selectedDate={selectedDate}
                            selectedTime={selectedTime}
                            onNext={handleRescheduleTour}
                            onBack={() => setViewMode('details')}
                            updateFormData={updateFormData}
                            rescheduleToken={token}
                            isSubmitting={actionLoading}
                        />

                        {actionLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CircularProgress />
                            </Box>
                        )}
                    </Box>
                )}
                        </Box>
                    )}
                </Box>
            </Box>
            <ContactFooter />
            <Footer />
        </>
    );
}

export default TourManagement;
